import { useEffect, useRef, useState } from "react";
import { Bell, ChevronDown, LogOut, User as UserIcon } from "lucide-react";
import type { PortalId } from "@/domain/screen";

export function TopHeader({ portal, userName, userRole, unreadCount = 0 }: {
  portal: PortalId;
  userName?: string;
  userRole?: string;
  unreadCount?: number;
}) {
  const [bellOpen, setBellOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const initials = (userName ?? portal)
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Close either dropdown on outside click.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setBellOpen(false);
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header className="top-header" style={{ justifyContent: "flex-end" }} ref={rootRef}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="topbar-pill subtle">Company</button>
          <button className="topbar-pill active">Portal</button>
        </div>

        <div style={{ position: "relative" }}>
          <button
            className="topbar-bell"
            style={{ border: "none", cursor: "pointer" }}
            onClick={() => { setBellOpen((v) => !v); setMenuOpen(false); }}
          >
            <Bell size={18} />
            {unreadCount > 0 && <span className="badge-count">{unreadCount}</span>}
          </button>
          {bellOpen && (
            <div className="dropdown-panel">
              <div className="dropdown-title">Notifications</div>
              {unreadCount > 0 ? (
                <div className="dropdown-item">You have {unreadCount} unread notification{unreadCount > 1 ? "s" : ""}.</div>
              ) : (
                <div className="dropdown-item muted">No new notifications.</div>
              )}
              <a className="dropdown-footer-link" href="/investor/notifications">View all notifications</a>
            </div>
          )}
        </div>

        <div style={{ position: "relative" }}>
          <button
            style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer" }}
            onClick={() => { setMenuOpen((v) => !v); setBellOpen(false); }}
          >
            <div className="avatar-circle">{initials}</div>
            <div style={{ lineHeight: 1.3, textAlign: "left" }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{userName ?? portal}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{userRole ?? portal}</div>
            </div>
            <ChevronDown size={16} color="var(--text-muted)" />
          </button>
          {menuOpen && (
            <div className="dropdown-panel" style={{ minWidth: 180 }}>
              <button className="dropdown-item as-button"><UserIcon size={14} /> My Profile</button>
              <button className="dropdown-item as-button"><LogOut size={14} /> Sign out</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
